import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Editor, NodeViewContent, NodeViewProps, NodeViewWrapper } from '@tiptap/react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { BlockNodeViewMenus } from './parts/block-node-view-menus';

import { useBlockInstanceActions } from '@/hooks/use-block-instance-actions';

import { getBlockStorage } from '@/utils/editor-utils';

import styles from './block-node-view.module.scss';
import { TextSelection } from '@tiptap/pm/state';

export const BlockNodeView = (props: NodeViewProps) => {
  const {
    node,
    editor,
    getPos,
    node: {
      attrs: { id, instanceId, name },
    },
  } = props;

  const [selected, setSelected] = useState(false);
  const [focusedWithin, setFocusedWithin] = useState(false);

  const focusedPos = useRef<number | null>(null);

  const { isOutOfSync, blockInstructionStatus, copyBlock, deleteBlockNode } = useBlockInstanceActions(props);

  useEffect(() => {
    const checkSelection = () => {
      const state = editor.state;
      const selection = state.selection;
      const nodeStart = getPos() ?? 0;
      const nodeEnd = nodeStart + node.nodeSize;

      const selFrom = selection.from;
      const selTo = selection.to;

      // Check if selection overlaps with this node
      return (
        selFrom !== selTo &&
        ((selFrom >= nodeStart && selFrom < nodeEnd) ||
          (selTo > nodeStart && selTo <= nodeEnd) ||
          (selFrom <= nodeStart && selTo >= nodeEnd))
      );
    };

    const update = () => {
      const { focusedBlockInstanceId, selectedBlockInstanceId } = getBlockStorage(editor as Editor);

      setSelected(selectedBlockInstanceId === instanceId);
      setFocusedWithin(focusedBlockInstanceId === instanceId || checkSelection());
    };

    editor.on('selectionUpdate', update);
    editor.on('transaction', update);

    update();

    return () => {
      editor.off('selectionUpdate', update);
      editor.off('transaction', update);
    };
  }, [editor, getPos, node, instanceId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const { metaKey, ctrlKey, key } = event;
      const isCmdPressed = metaKey || ctrlKey;

      if (isCmdPressed && key === 'c') {
        copyBlock();
      }
    };

    selected && document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected, editor, copyBlock]);

  return (
    <NodeViewWrapper
      data-id={id}
      data-instanceid={instanceId}
      data-testid="block-node-view"
      data-selected={selected}
      data-outofsync={isOutOfSync}
      data-focusedwithin={focusedWithin}
      className={clsx(styles.blockNodeView, 'block-node-view', {
        [styles.outOfSync]: isOutOfSync,
        [styles.selected]: selected && !focusedWithin,
        [styles.focusedWithin]: !selected && focusedWithin,
      })}
    >
      <div
        tabIndex={0}
        contentEditable={false}
        className={styles.blockNodeViewHeader}
        onKeyDown={(event) => {
          if (selected && event.key === 'Backspace') {
            deleteBlockNode();
          }
        }}
        onClick={() => {
          const currentSelection = editor.state.selection;
          if (currentSelection.from !== currentSelection.to) {
            // Clear browser's native text selection
            window.getSelection()?.removeAllRanges();

            // If there's a text selection, clear it first
            editor
              .chain()
              .command(({ state, dispatch }) => {
                const tr = state.tr;
                // Clear current selection by setting cursor at block start
                tr.setSelection(TextSelection.create(state.doc, getPos()!));
                dispatch?.(tr);
                return true;
              })
              .run();
          }

          editor.commands.selectBlock(id, instanceId);
        }}
      >
        <div className={styles.blockLabel}>
          <Icon type="blockchain-01-solid" />
          <span title={name} className={clsx('ellipsis-text', styles.name)}>
            {name}
          </span>
        </div>
        <BlockNodeViewMenus
          blockId={id}
          nodeViewProps={props}
          onMenuOpen={() => {
            focusedPos.current = editor.state.selection.from;
          }}
          onMenuClose={() => {
            // returns cursor where it was before
            editor.commands.focus(focusedPos.current, { scrollIntoView: false });
            focusedPos.current = null;
          }}
          blockInstructionStatus={blockInstructionStatus}
        />
      </div>
      <NodeViewContent />
    </NodeViewWrapper>
  );
};
